import { Component, ReactNode } from 'react';
import { connect } from 'react-redux';
import Select, { MultiValueProps, components } from 'react-select';
import AsyncSelect from 'react-select/async';
import AsyncCreatableSelect from 'react-select/async-creatable';
import CreatableSelect from 'react-select/creatable';
import { Icon } from 'components/common/icons';

import type {
  FormatOptionLabelContext,
  InputProps,
  MenuProps,
  MenuPosition,
  OptionProps,
  Props as ReactSelectProps
} from 'react-select';
import type { ReduxRootState } from 'types';

type Props = ReactSelectProps & {
  autoComplete?: string;
  ariaLabel?: string;
  async?: boolean;
  creatable?: boolean;
  dropdownStyles?: boolean;
  error?: string;
  field?: string;
  hideDropdownIndicator?: boolean;
  inputIsHidden?: boolean;
  inputValue?: string;
  keepContainerOpen?: boolean;
  menuAlign?: 'left' | 'right';
  menuWidth?: string;
  selectElementRef?: any;
  selectRef?: any;
  showLabelForSelectedElement?: boolean;
  style?: React.CSSProperties;
  usePortal?: boolean;
};

const SelectInputClass = connect((state: ReduxRootState) => ({
  theme: state.theme
}))(
  class extends Component<Props & { theme: ReduxRootState['theme'] }> {
    // internal variables
    state = { mounted: false, usePortal: false };

    portalElement: HTMLDivElement;
    selectElement: HTMLDivElement;

    componentDidMount() {
      let usePortal = this.props.usePortal;
      this.portalElement = document.querySelector('.select-menu-root');

      if (this.props.usePortal === undefined) {
        // render the select menu items in a portal if we are in a modal
        // this is done to properly show the menu items with overflow hidden
        let el = this.selectElement;
        while (el && el.classList && !el.classList.contains('modal')) {
          el = el.parentElement as HTMLDivElement;
        }
        if (el && el.classList && el.classList.contains('modal')) {
          usePortal = true;
        }
      }
      this.setState({ mounted: true, usePortal });
    }

    formatOptionLabel = (
      data: {
        endDate?: any;
        helpText?: string | ReactNode;
        label: string;
        startDate?: string;
      },
      { context }: { context: FormatOptionLabelContext }
    ) =>
      context === 'menu' && data.helpText ? (
        <>
          <div>{data.label}</div>
          <div
            style={{
              color: this.props.theme.textColorLighter,
              fontSize: '1.2rem',
              lineHeight: 1.3,
              padding: '2px 0'
            }}
          >
            {data.helpText}
          </div>
        </>
      ) : (
        data.label
      );

    Input = (props: InputProps) => (
      <components.Input
        {...props}
        autoComplete="nope"
        isHidden={this.props.inputIsHidden}
      />
    );

    Menu = (props: MenuProps) => (
      <div
        style={{
          maxWidth: `calc(100vw - 60px)`,
          position: 'relative',
          width: this.props.menuWidth || '100%',
          ...(this.props.menuAlign === 'right'
            ? {
                position: 'absolute',
                right: 0
              }
            : {})
        }}
        {...(this.props.keepContainerOpen ? { 'data-keep-open': true } : {})}
      >
        <components.Menu {...props}>{props.children}</components.Menu>
      </div>
    );

    MultiValue = (
      props: MultiValueProps<Record<'error' | 'label' | 'value', string>>
    ) => {
      const { showLabelForSelectedElement, theme } = this.props;
      const error = props.data.error;

      const style = error
        ? {
            backgroundColor: theme.dangerLightColor,
            hoverBackgroundColor: 'rgba(0, 0, 0, 0.05)',
            hoverColor: theme.dangerLightTextColor,
            textColor: theme.dangerLightTextColor
          }
        : {
            backgroundColor: theme.lightGrey,
            hoverBackgroundColor: theme.lightGreyActive,
            hoverColor: theme.textColorLight,
            textColor: theme.textColorLight
          };

      return (
        <div
          className="multi-value-select"
          {...(error ? { 'data-tooltip': error } : {})}
        >
          <components.MultiValue {...props}>
            {showLabelForSelectedElement ? props.data.label : props.data.value}
          </components.MultiValue>

          <style jsx>{`
            .multi-value-select :global(.react-select__multi-value) {
              background-color: ${style.backgroundColor};
              border-radius: 5px;
              margin: 3px 8px 3px 0px;
            }
            .multi-value-select :global(.react-select__multi-value__label) {
              color: ${style.textColor};
              cursor: default;
              font-size: 1.5rem;
              font-weight: 600;
              padding: 4px 6px 5px 10px;
            }
            .multi-value-select :global(.react-select__multi-value__remove) {
              border-radius: 5px;
              color: ${style.textColor};
              margin: 3px;
              padding-left: 3px;
              padding-right: 3px;
            }
            .multi-value-select
              :global(.react-select__multi-value__remove:hover) {
              background-color: ${style.hoverBackgroundColor};
              color: ${style.hoverColor};
            }
            .multi-value-select
              :global(.react-select__multi-value__remove svg) {
              height: 16px;
              width: 16px;
            }
          `}</style>
        </div>
      );
    };

    Option = (props: OptionProps) => {
      const { children, ...propsWithoutChildren } = props;
      return (
        <components.Option {...propsWithoutChildren}>
          {children}

          {props.isSelected ? (
            <Icon
              icon="check"
              style={{
                color: this.props.theme.textColor,
                fontSize: '1rem',
                marginLeft: 30,
                position: 'absolute',
                right: 15,
                top: 12
              }}
            />
          ) : null}
        </components.Option>
      );
    };

    render() {
      const {
        ariaLabel,
        async,
        components,
        creatable,
        dropdownStyles = false,
        error,
        hideDropdownIndicator,
        menuPlacement = 'auto',
        selectElementRef,
        selectRef,
        style,
        theme,
        ...params
      } = this.props;

      const { mounted, usePortal } = this.state;

      const selectProps = {
        className: 'react-select-container',
        classNamePrefix: 'react-select',
        components: {
          Input: this.Input,
          Menu: this.Menu,
          MultiValue: this.MultiValue,
          Option: this.Option,
          ...components
        },
        formatOptionLabel: this.formatOptionLabel,
        menuPlacement,
        menuPortalTarget: mounted && usePortal ? this.portalElement : null,
        menuPosition: (mounted && usePortal
          ? 'fixed'
          : 'absolute') as MenuPosition,
        ref: selectRef,
        ...(ariaLabel ? { 'aria-label': ariaLabel } : {}),
        ...params
      };

      return (
        <div
          className="select"
          ref={(el) => {
            this.selectElement = el;
            if (selectElementRef) selectElementRef(el);
          }}
          style={style}
          {...(ariaLabel ? { 'aria-label': ariaLabel } : {})}
        >
          {creatable && async ? (
            <AsyncCreatableSelect {...selectProps} />
          ) : creatable ? (
            <CreatableSelect {...selectProps} />
          ) : async ? (
            <AsyncSelect {...selectProps} />
          ) : (
            <Select {...selectProps} />
          )}

          <style jsx>{`
            .select {
              font-size: ${dropdownStyles ? '1.3rem' : '1.5rem'};
              font-weight: 400;
            }
            .select :global(.react-select__control) {
              background-color: white;
              border: 1px solid
                ${error ? theme.dangerColor : theme.lightGreyDark};
              border-radius: ${theme.inputBorderRadius};
              box-shadow: none;
              color: ${theme.textColor};
              cursor: pointer;
              font-family: inherit;
              min-height: ${dropdownStyles ? 'auto' : '35px'};
              transition: border-color 0.2s;
            }
            .select :global(.react-select__control:hover) {
              border-color: ${error
                ? theme.dangerColor
                : theme.lightGreyDarkActive};
            }
            .select :global(.react-select__control--is-focused),
            .select :global(.react-select__control--is-focused:hover) {
              border-color: ${error ? theme.dangerColor : '#a1a8aa'};
            }
            .select :global(.react-select__control--is-disabled) {
              background-color: ${theme.lightGreyLighter};
              color: ${theme.textColorLighter};
              opacity: 0.7;
            }
            .select :global(.react-select__value-container) {
              padding: ${creatable
                ? '5px 8px'
                : dropdownStyles
                ? '2px 8px 1px'
                : '2px 8px'};
            }
            .select :global(.react-select__input-container) {
              font-family: inherit;
              margin: 0;
              padding: 0;
            }
            .select :global(.react-select__input) {
              outline: 0;
            }
            .select :global(.react-select__single-value) {
              color: ${theme.textColor};
              font-weight: 400;
              margin: 0 2px;
              text-overflow: ellipsis;
            }
            .select :global(.react-select__indicators) {
              align-items: center;
              align-self: stretch;
              box-sizing: border-box;
              display: ${creatable ? 'none' : 'flex'};
              flex-shrink: 0;
            }
            .select :global(.react-select__indicator-separator) {
              display: none;
            }
            .select :global(.react-select__dropdown-indicator) {
              box-sizing: border-box;
              color: #999;
              display: ${hideDropdownIndicator ? 'none' : 'flex'};
              padding: ${dropdownStyles ? '4px' : '6px'};
              transition: color 150ms;
            }
            .select :global(.react-select__dropdown-indicator > svg) {
              display: inline-block;
              fill: currentColor;
              line-height: 1;
              stroke: currentColor;
              stroke-width: 0;
            }
            .select :global(.react-select__clear-indicator) {
              padding: 6px;
            }
            .select :global(.react-select__menu),
            :global(.select-menu-root .react-select__menu) {
              border-radius: ${theme.dropdownBorderRadius};
              box-shadow: ${theme.dropdownShadow};
              z-index: 3;
            }
            .select :global(.react-select__menu-list),
            :global(.select-menu-root .react-select__menu-list) {
              padding-bottom: 8px;
              padding-top: 8px;
              padding-left: 5px;
              padding-right: 5px;
            }
            .select :global(.react-select__option),
            :global(.select-menu-root .react-select__option) {
              border-radius: ${theme.buttonBorderRadius};
              color: ${theme.textColorLight};
              cursor: pointer;
              font-size: 1.4rem;
              font-weight: 600;
              padding: 8px 47px 8px 12px;
              position: relative;
              transition: background-color 0.3s, color 0.3s;
            }
            .select :global(.react-select__option:active),
            :global(.select-menu-root .react-select__option:active) {
              background-color: ${theme.lightGrey};
            }
            .select :global(.react-select__option--is-focused),
            .select
              :global(
                .react-select__option--is-selected.react-select__option--is-focused
              ),
            :global(.select-menu-root .react-select__option--is-focused),
            :global(
                .select-menu-root
                  .react-select__option--is-selected.react-select__option--is-focused
              ) {
              background-color: ${theme.lightGreyLighter};
            }
            .select :global(.react-select__option--is-selected),
            :global(.select-menu-root .react-select__option--is-selected) {
              background-color: inherit;
              color: ${theme.textColor};
              font-weight: 700;
            }
            .select :global(.react-select__option--is-selected:active),
            :global(
                .select-menu-root .react-select__option--is-selected:active
              ) {
              background-color: ${theme.lightGreyDark};
            }
            .select :global(.react-select__option--is-disabled),
            .select :global(.react-select__option--is-disabled:active),
            :global(.select-menu-root .react-select__option--is-disabled),
            :global(
                .select-menu-root .react-select__option--is-disabled:active
              ) {
              background-color: white;
              cursor: default;
              opacity: 0.5;
            }
            :global(.select-menu-root) {
              font-size: 1.5rem;
              position: fixed;
              top: 0;
              left: 0;
              z-index: 240;
            }
          `}</style>
        </div>
      );
    }
  }
);

export default SelectInputClass;
